import { type ComposerDraft, EMPTY_COMPOSER_DRAFT } from "./composerSend";

/** Where a composer's send stands: in flight, or failed with the message the composer shows. */
export type ComposerStatus = { sending: boolean; error: string | null };

const IDLE: ComposerStatus = Object.freeze({ sending: false, error: null });

/**
 * A composer's drafts, one per key, and the state of its send, held outside React.
 *
 * Whatever renders the composer subscribes (`useSyncExternalStore`) and re-renders on a keystroke; the component that
 * owns the store does not, so a page holding the drafts for every row stays still while one of them is typed into.
 * Every read returns the same object until a write changes it, which is what `useSyncExternalStore` needs of a snapshot.
 */
export class ComposerDraftStore {
  private drafts: ReadonlyMap<string, ComposerDraft> = new Map();
  private status: ComposerStatus = IDLE;
  private readonly listeners = new Set<() => void>();

  /** Registers a listener for every write; the returned function unregisters it. */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Where the send stands. */
  public readonly getStatus = (): ComposerStatus => this.status;

  /** The draft kept under `key`; the empty draft when nothing is, or when there is no key. */
  public draft(key: string | null): ComposerDraft {
    if (key == null) return EMPTY_COMPOSER_DRAFT;
    return this.drafts.get(key) ?? EMPTY_COMPOSER_DRAFT;
  }

  /** Replaces the draft under `key` with what `change` makes of it, and clears a failed send's message. */
  public updateDraft(key: string, change: (current: ComposerDraft) => ComposerDraft): void {
    this.drafts = new Map(this.drafts).set(key, change(this.draft(key)));
    if (this.status.error != null) this.status = { ...this.status, error: null };
    this.emit();
  }

  /** Drops the draft under `key`, as a send that went out does. */
  public clearDraft(key: string): void {
    if (!this.drafts.has(key)) return;
    const next = new Map(this.drafts);
    next.delete(key);
    this.drafts = next;
    this.emit();
  }

  /** Records where the send stands. */
  public setStatus(status: ComposerStatus): void {
    if (status.sending === this.status.sending && status.error === this.status.error) return;
    this.status = status;
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
